import {
    getFriendsScores,
    getUsersScores,
    LeaderboardEntry,
    LeaderboardUser,
    postScore,
} from '@sgorg/game-backend-client';
import Core from '../Core/Core';
import DateUtils from '../Utils/DateUtils';
import { ILeaderBoardPlayer } from '../UI/PopUps/Social/LeaderBoard';
import { ILogger } from '../Core/Logger';
import getLogger from '../Core/Logger';

interface ISDKLeaderboardUser extends LeaderboardUser {
    avatarUrl: string;
}

export interface ISDKLeaderboardPlayer extends LeaderboardEntry {
    user: ISDKLeaderboardUser;
}

export interface ISDKleaderboards {
    getLeaderBoardData: (id: string, instance: string) => Promise<any>;
    setLeaderBoardData: (id: string, instance: string, score: number) => Promise<unknown>;
    setLevelLeaderBoardData: (level: string, score: number) => Promise<boolean>;
    setWeeklyLeaderBoardData: (score: number) => Promise<void>;
    getCurrentWeekID: () => string;
    formatLeaderBoardData: (data: any[]) => ILeaderBoardPlayer[];
    getLevelLeaderBoardData: (level: string) => Promise<ISDKLeaderboardPlayer[]>;
}

const logger: ILogger = getLogger('[LEADERBOARDS]:');

export class SDKleaderboards implements ISDKleaderboards {
    public backend: any;

    constructor(platform: any) {
        this.backend = platform;
    }

    public async getLeaderBoardData(id: string, instance: string) {
        if (
            !this.backend ||
            !this.backend.isInitialized() ||
            !(await this.backend.checkConnection())
        ) {
            return [];
        }

        try {
            const leaderboard = await getFriendsScores({
                leaderboardName: id,
                instanceId: instance,
            });
            const friends = await Core.sdk.messages.getFriends();
            const playerAvatar = FBInstant.player.getPhoto() || '';
            const playerName = FBInstant.player.getName() || '';

            return leaderboard.map(data => {
                const fbData = friends.find((user: any) => user.userId === data.user.playerId);
                const avatar = fbData && fbData.avatar ? fbData.avatar : '';
                const fbName = fbData && fbData.name ? fbData.name : '';
                return {
                    score: data.score,
                    rank: data.rank,
                    user: {
                        handle: data.user.isCurrentUser ? playerName : data.user.handle || fbName,
                        isCurrentUser: data.user.isCurrentUser,
                        avatarUrl: data.user.isCurrentUser ? playerAvatar : avatar,
                        playerId: data.user.playerId,
                        id: data.user.id,
                    },
                };
            });
        } catch (err) {
            return [];
        }
    }

    public async setLeaderBoardData(id: string, instance: string, score: number) {
        if (
            !this.backend ||
            !this.backend.isInitialized() ||
            !(await this.backend.checkConnection())
        ) {
            return Promise.reject('backend not connected');
        }
        return postScore({ leaderboardName: id, instanceId: instance, score });
    }

    public async setLevelLeaderBoardData(levelId: string, score: number) {
        try {
            const leaderBoardId = 'levelLeaderboards';
            await this.setLeaderBoardData(leaderBoardId, levelId, score);
            return true;
        } catch (e) {
            logger.debug(['error setting level leaderboard progress', e]);
            return false;
        }
    }

    public async setWeeklyLeaderBoardData(score: number) {
        try {
            const weekId = this.getCurrentWeekID();
            const lbId = 'weeklyLeaderboards';
            const oldScore = await this.getCurrentPlayerScore(lbId, weekId);
            const add = oldScore && oldScore.score ? oldScore.score.valueOf() : 0;
            await this.setLeaderBoardData('weeklyLeaderboards', weekId, add + score);
        } catch (e) {
            logger.debug(['error setting weekly leaderboard progress', e]);
        }
    }

    public async getWeeklyLeaderBoardData(levelId: string) {
        try {
            const leaderBoardId = 'levelLeaderboards';
            const data = await this.getLeaderBoardData(leaderBoardId, levelId);
            return data;
        } catch (e) {
            logger.debug(['error getting level leaderboard progress', e]);
            return [];
        }
    }

    public getCurrentWeekID(): string {
        const week = DateUtils.getCurrentWeek();
        const year = DateUtils.getWeekYear();
        return 'week_' + week.toString(10) + '_' + year.toString();
    }

    public formatLeaderBoardData(data: any[]): ILeaderBoardPlayer[] {
        return data.map(element => {
            return {
                id: element.user.playerId,
                name: element.user.handle,
                stars: element.score,
                isUser: element.user.isCurrentUser,
                url: element.user.avatarUrl,
            };
        });
    }

    public async getLevelLeaderBoardData(levelId: string) {
        try {
            const leaderBoardId = 'levelLeaderboards';
            const data = await this.getLeaderBoardData(leaderBoardId, levelId);
            return data;
        } catch (e) {
            logger.debug(['error getting level leaderboard progress', e]);
            return [];
        }
    }

    // this will only work with
    public async getCurrentPlayerScore(leaderboardName: string, instanceId: string) {
        const currentPlayerId = Core.sdk.user.userId;
        return (await getUsersScores({
            leaderboardName,
            instanceId,
            players: [currentPlayerId],
        }))[0];
    }
}
